import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, Home, Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CATS = ["Criminal Law", "Civil Law", "Supreme Court", "Bombay High Court", "Legal Updates", "Government Circulars"];

const EMPTY = { title: "", category: "Criminal Law", excerpt: "", content: "", reading_time: "5 min read", thumbnail: "", published: true };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const a = await api.get("/articles/admin");
      setArticles(a.data);
      const e = await api.get("/enquiries");
      setEnquiries(e.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (a) => { setEditing(a.id); setForm({...EMPTY, ...a}); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing === "new") {
        await api.post("/articles", form);
        toast.success("Article created");
      } else {
        await api.put(`/articles/${editing}`, form);
        toast.success("Article updated");
      }
      cancel(); load();
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/articles/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <header className="border-b border-white/5 sticky top-0 bg-black/80 backdrop-blur-xl z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div>
            <div className="font-cinzel text-[10px] tracking-[0.35em] text-[#C8A45D]">NEXUS LEGAL</div>
            <div className="font-serif-display text-lg text-white/90">Chamber Console</div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="btn-ghost text-[10px] px-4 py-2" data-testid="admin-view-site"><Home size={12}/> Site</Link>
            <button onClick={doLogout} className="btn-ghost text-[10px] px-4 py-2" data-testid="admin-logout"><LogOut size={12}/> Sign Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <div className="mb-10 flex items-center gap-6">
          <button onClick={()=>{setTab("articles"); cancel();}} className={`font-cinzel text-[11px] tracking-[0.3em] pb-2 border-b ${tab==="articles"?"text-[#C8A45D] border-[#C8A45D]":"text-white/50 border-transparent"}`} data-testid="tab-articles">ARTICLES ({articles.length})</button>
          <button onClick={()=>{setTab("enquiries"); cancel();}} className={`font-cinzel text-[11px] tracking-[0.3em] pb-2 border-b ${tab==="enquiries"?"text-[#C8A45D] border-[#C8A45D]":"text-white/50 border-transparent"}`} data-testid="tab-enquiries">ENQUIRIES ({enquiries.length})</button>
        </div>

        {tab === "articles" && (
          <>
            {editing === null ? (
              <>
                <div className="flex justify-between items-center mb-8">
                  <h2 className="font-serif-display text-3xl text-white">Articles</h2>
                  <button onClick={startNew} className="btn-primary text-[10px] px-5 py-3" data-testid="btn-new-article"><Plus size={12}/> New Article</button>
                </div>
                <div className="space-y-2">
                  {articles.map((a) => (
                    <div key={a.id} className="flex items-center justify-between p-5 border border-white/5 hover:border-white/10 transition-colors" data-testid={`article-row-${a.id}`}>
                      <div className="min-w-0">
                        <div className="text-[10px] font-cinzel tracking-[0.3em] text-[#C8A45D] mb-1">{a.category}</div>
                        <div className="font-serif-display text-xl text-white/95 truncate">{a.title}</div>
                        <div className="text-xs text-white/40 mt-1">{new Date(a.created_at).toLocaleDateString()} · {a.reading_time}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={()=>startEdit(a)} className="p-2 border border-white/10 hover:border-[#C8A45D] hover:text-[#C8A45D] text-white/70 transition-colors" data-testid={`edit-${a.id}`}><Edit3 size={14}/></button>
                        <button onClick={()=>del(a.id)} className="p-2 border border-white/10 hover:border-red-500 hover:text-red-500 text-white/70 transition-colors" data-testid={`delete-${a.id}`}><Trash2 size={14}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <form onSubmit={save} className="space-y-6 max-w-3xl" data-testid="article-form">
                <div className="flex justify-between items-center">
                  <h2 className="font-serif-display text-3xl text-white">{editing === "new" ? "New Article" : "Edit Article"}</h2>
                  <button type="button" onClick={cancel} className="btn-ghost text-[10px] px-4 py-2">Cancel</button>
                </div>
                <input required className="nx-input" placeholder="Title *" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="form-title"/>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <select className="nx-input" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} data-testid="form-category">
                    {CATS.map(c=><option key={c} value={c} className="bg-[#121212]">{c}</option>)}
                  </select>
                  <input className="nx-input" placeholder="Reading time (e.g. 5 min read)" value={form.reading_time} onChange={(e)=>setForm({...form, reading_time:e.target.value})} data-testid="form-reading-time"/>
                </div>
                <input className="nx-input" placeholder="Thumbnail image URL" value={form.thumbnail} onChange={(e)=>setForm({...form, thumbnail:e.target.value})} data-testid="form-thumbnail"/>
                <textarea rows="2" className="nx-input" placeholder="Excerpt / summary" value={form.excerpt} onChange={(e)=>setForm({...form, excerpt:e.target.value})} data-testid="form-excerpt"/>
                <textarea rows="10" className="nx-input" placeholder="Full article content" value={form.content} onChange={(e)=>setForm({...form, content:e.target.value})} data-testid="form-content"/>
                <label className="flex items-center gap-3 text-white/70 text-sm font-body">
                  <input type="checkbox" checked={form.published} onChange={(e)=>setForm({...form, published:e.target.checked})} data-testid="form-published"/>
                  Published (visible on public site)
                </label>
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="form-submit">
                  {loading ? "Saving…" : editing === "new" ? "Publish" : "Update"}
                </button>
              </form>
            )}
          </>
        )}

        {tab === "enquiries" && (
          <>
            <h2 className="font-serif-display text-3xl text-white mb-8">Enquiries</h2>
            {enquiries.length === 0 && <div className="text-white/40 font-cinzel text-xs tracking-[0.3em]">NO ENQUIRIES YET</div>}
            <div className="space-y-4">
              {enquiries.map((e) => (
                <div key={e.id} className="p-6 border border-white/5" data-testid={`enquiry-${e.id}`}>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 mb-4">
                    <div>
                      <div className="font-serif-display text-2xl text-white">{e.name}</div>
                      <div className="text-white/50 text-sm font-body flex gap-4 mt-1"><span>{e.email}</span><span>·</span><span>{e.phone}</span></div>
                    </div>
                    <div className="text-[10px] font-cinzel tracking-[0.3em] text-white/40">{new Date(e.created_at).toLocaleString()}</div>
                  </div>
                  {e.subject && <div className="font-cinzel text-[10px] tracking-[0.3em] text-[#C8A45D] mb-2">{e.subject.toUpperCase()}</div>}
                  <p className="text-white/70 font-body text-sm leading-relaxed">{e.message}</p>
                  <div className="mt-4 flex gap-3">
                    <a href={`mailto:${e.email}`} className="text-[10px] font-cinzel tracking-[0.3em] text-[#C8A45D] hover:underline"><Mail size={10} className="inline mr-1"/> REPLY</a>
                    <a href={`tel:${e.phone}`} className="text-[10px] font-cinzel tracking-[0.3em] text-white/60 hover:text-[#C8A45D]">CALL</a>
                    <a href={`https://wa.me/${e.phone.replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="text-[10px] font-cinzel tracking-[0.3em] text-white/60 hover:text-[#C8A45D]">WHATSAPP</a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
